import { Inject, Injectable } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { environment } from "environments/environment";
import { LocalStorageService } from "framework/localstorage.service";

declare global {
  interface Window {
    Osano: any;
    clarity: any;
  }
}

@Injectable({
  providedIn: "root",
})
export class ClarityService {
  constructor(@Inject(DOCUMENT) private readonly document: Document,
              private _localStorage: LocalStorageService) {}

  public clarityQueue = [];

  public isInitialized = false;

  private clarityScriptLoaded = false;
  
  /** ID used to identify the Clarity script tag in the DOM for cleanup */
  private readonly CLARITY_SCRIPT_ID = "ms-clarity-tag";
  
  /** ID used to identify the Osano script tag in the DOM */
  private readonly OSANO_SCRIPT_ID = "osano-cmp-script";

  public initialize(): void {
    // Load Osano script first (required for consent management)
    this.loadOsanoScript();
    
    if (!this.isClarityIdValid(environment["clarity_id"])) {
      return;
    }

    // Reset any state from previous app boots/navigation.
    this.isInitialized = false;
    this.clarityScriptLoaded = false;

    // Defensive cleanup: if Clarity was injected by an older build/session, remove it.
    this.removeClarityCompletely();

    // DO NOT load Clarity script yet - wait for consent
    // Setup Osano consent listeners first
    this.setupOsanoListeners();
  }

  private loadOsanoScript(): void {
    // Check if Osano script is already loaded
    if (document.getElementById(this.OSANO_SCRIPT_ID)) {
      console.log('Osano script already loaded');
      return;
    }

    const customerId = environment["osano_customer_id"];
    const configId = environment["osano_config_id"];

    if (!customerId || !configId) {
      console.error('Osano Customer ID or Config ID not configured in environment');
      return;
    }

    const scriptElement = this.document.createElement("script");
    scriptElement.id = this.OSANO_SCRIPT_ID;
    scriptElement.src = `https://cmp.osano.com/${customerId}/${configId}/osano.js`;
    this.document.head.appendChild(scriptElement);
    console.log('Osano script loaded from environment configuration');
  }

  private loadClarityScript(): void {
    if (this.clarityScriptLoaded) {
      return;
    }

    this.ensureClarityStub();

    const scriptElement = this.document.createElement("script");
    scriptElement.id = this.CLARITY_SCRIPT_ID;
    scriptElement.async = true;
    scriptElement.src = `https://www.clarity.ms/tag/${environment["clarity_id"]}`;
    this.document.head.appendChild(scriptElement);

    this.clarityScriptLoaded = true;
    console.log("Clarity script tag injected after consent granted.");
  }

  private ensureClarityStub(): void {
    if (typeof window.clarity === "function") {
      return;
    }

    const clarityFn = function (...args: any[]) {
      (clarityFn as any).q = (clarityFn as any).q || [];
      (clarityFn as any).q.push(args);
    };
    (clarityFn as any).q = (clarityFn as any).q || [];
    window.clarity = clarityFn;
  }

  private removeClarityCompletely(): void {
    this.removeClarityScriptsFromDom();
    this.disableClarityGlobal();
    this.clearClarityCookies();
    this._localStorage.setItem("isClarityBooted", false);
    this.isInitialized = false;
    this.clarityScriptLoaded = false;
  }

  private removeClarityScriptsFromDom(): void {
    const scripts = Array.from(this.document.getElementsByTagName("script"));
    for (const script of scripts) {
      const src = script.getAttribute("src") || "";
      const content = script.textContent || "";
      const isClaritySrc = src.includes("clarity.ms/tag/") || src.includes("www.clarity.ms/tag/");
      const isClarityInline = content.includes("clarity.ms/tag/") || content.includes("www.clarity.ms/tag/");

      if (script.id === this.CLARITY_SCRIPT_ID || isClaritySrc || isClarityInline) {
        script.parentNode?.removeChild(script);
      }
    }
  }

  private disableClarityGlobal(): void {
    if (typeof window.clarity !== "undefined") {
      try {
        // Replace with a no-op function to block any queued or future calls
        (window as any).clarity = function() {
          console.warn('Clarity is disabled due to denied consent');
        };
        // Then try to delete it completely
        delete (window as any).clarity;
      } catch {
        // If delete fails, at least replace with no-op
        (window as any).clarity = function() {
          console.warn('Clarity is disabled due to denied consent');
        };
      }
    }
  }

  private clearClarityCookies(): void {
    // Clarity uses multiple cookies across different domains
    const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
    
    // Clarity and Microsoft tracking cookies to remove
    const cookieNames = [
      '_clck', '_clsk', '_cls', '_clx',  // Clarity cookies
      'ANONCHK', 'CLID', 'MR', 'MUID', 'SM', 'SRM_B'  // Microsoft cookies
    ];
    
    // Extract base domain from current hostname
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const baseDomain = parts.length > 1 ? '.' + parts.slice(-2).join('.') : hostname;
    
    // Try multiple domain variations
    // Note: Due to browser security, can only clear cookies for current domain and parent domains
    const domains = [
      '',  // Current path
      hostname,  // Exact hostname (e.g., pa-intl-qa.fadv.net)
      baseDomain,  // Base domain (e.g., .fadv.net) - works cross-subdomain within fadv.net
      '.fadv.com',  // Alternate TLD if used
      '.fadv.net'   // Parent domain
      // Cannot clear: .switch.fadv.com (different subdomain), .clarity.ms, .microsoft.com (different domains)
    ];
    
    console.log('Clearing Clarity cookies for domains:', domains);
    
    // Clear cookies for all combinations
    cookieNames.forEach(name => {
      domains.forEach(domain => {
        const domainStr = domain ? `; domain=${domain}` : '';
        // Try with and without secure flag
        this.document.cookie = `${name}=; expires=${expire}; path=/${domainStr}`;
        this.document.cookie = `${name}=; expires=${expire}; path=/${domainStr}; secure`;
        this.document.cookie = `${name}=; expires=${expire}; path=/${domainStr}; SameSite=None; secure`;
      });
      
      // Also try clearing from root domain without specifying domain
      this.document.cookie = `${name}=; expires=${expire}; path=/`;
    });
    
    // Log remaining cookies for debugging
    const remainingCookies = document.cookie.split(';').map(c => c.trim()).filter(c => 
      cookieNames.some(name => c.startsWith(name + '='))
    );
    if (remainingCookies.length > 0) {
      console.warn('⚠️ Some Clarity cookies could not be cleared:', remainingCookies);
      console.warn('⚠️ Microsoft domain cookies (MUID, ANONCHK, etc.) cannot be cleared via JavaScript');
    } else {
      console.log('✓ All accessible Clarity cookies cleared');
    }
  }

  private setupOsanoListeners(): void {
    const self = this;
    let userHasInteracted = false;
    let consentAlreadyHandled = false;  // Prevents double-processing when consent-saved fires before initialized
    let osanoHasInitialized = false;    // Tracks if osano-cm-initialized has fired

    // Check if user came from Authy redirect (cross-domain scenario)
    const isAuthyCallback = this._localStorage.getItem("isAuthyCallback") === true;
    const referrer = document.referrer.toLowerCase();
    const isFromAuthy = isAuthyCallback || referrer.includes('authy') || referrer.includes('b2clogin.com');
    
    if (isFromAuthy) {
      console.log('🔄 Detected Authy redirect - will honor cross-domain consent');
    } else {
      console.log('📱 Direct PA load - checking for stored consent or requiring user interaction');
    }

    // Define consent handler function
    const sendClarityConsent = (osanoConsent: any, isUserAction: boolean = false) => {
      console.log('Osano consent received:', osanoConsent);
      console.log('Is user action:', isUserAction);
      
      // Handle case where user hasn't made a choice yet
      // Osano returns 'ACCEPT', 'DENY', or undefined/null (no choice made)
      // Default to 'denied' if no explicit consent given (GDPR compliance)
      // Only grant if explicitly set to 'ACCEPT' (strict opt-in)
      const hasAnalyticsConsent = osanoConsent?.ANALYTICS === 'ACCEPT';
      const hasMarketingConsent = osanoConsent?.MARKETING === 'ACCEPT';
      
      const analyticsConsent = hasAnalyticsConsent ? 'granted' : 'denied';
      const marketingConsent = hasMarketingConsent ? 'granted' : 'denied';
      
      console.log('Raw Osano ANALYTICS value:', osanoConsent?.ANALYTICS);
      console.log('Raw Osano MARKETING value:', osanoConsent?.MARKETING);
      console.log('Analytics consent status:', analyticsConsent);
      console.log('Marketing consent status:', marketingConsent);
      console.log('User has interacted:', userHasInteracted);
      
      // CRITICAL: Determine if Clarity should load based on scenario
      // Scenario 1: Authy redirect (cross-domain) - honor stored consent immediately
      // Scenario 2: Direct PA load without prior consent - DENY until user explicitly accepts
      // Scenario 3: Direct PA load with prior consent - honor stored consent
      if (analyticsConsent === 'granted' && !self.clarityScriptLoaded) {
        let shouldLoadClarity = false;
        let loadReason = '';
        
        // Scenario 1: Cross-domain from Authy redirect
        if (isFromAuthy) {
          shouldLoadClarity = true;
          loadReason = 'Cross-domain consent from Authy redirect';
        }
        // Scenario 2 & 3: Direct PA load
        else {
          // User explicitly accepted in this session (clicked Accept button)
          if (isUserAction) {
            shouldLoadClarity = true;
            loadReason = 'User accepted in this session';
          }
          // Stored consent from previous session
          // GDPR Note: If Osano returns ACCEPT, it means user explicitly accepted before
          // We honor this stored consent as it represents a valid prior consent
          else if (!isUserAction) {
            shouldLoadClarity = true;
            loadReason = 'Stored consent from previous session (user accepted before)';
          }
        }
        
        if (shouldLoadClarity) {
          console.log('✅ Analytics consent granted - loading Clarity script');
          console.log('📋 Source:', loadReason);
          
          self.loadClarityScript();

          // Queue consent immediately (Clarity will process once its tag script loads).
          if (typeof window.clarity === "function") {
            window.clarity('consent', 'update', {
              ad_storage: marketingConsent,
              analytics_storage: analyticsConsent,
            });
          }

          self.isInitialized = true;
          userHasInteracted = true;
        } else {
          console.warn('⚠️ Consent is granted but conditions not met - waiting');
          self.isInitialized = false;
        }
      } else if (analyticsConsent === 'denied') {
        console.log('======= Analytics consent DENIED =======');
        console.log('Clarity script loaded:', self.clarityScriptLoaded);
        console.log('Window.clarity exists:', typeof window.clarity !== 'undefined');
        console.log('Source: ' + (isUserAction ? 'User denied in this session' : 'Stored denial (cross-domain or previous session)'));
        
        // CRITICAL: Immediately block and remove Clarity to prevent ANY MS Collect requests
        // This must happen regardless of current state
        
        // First, send denial to Clarity if it's somehow running
        if (typeof window.clarity === "function") {
          try {
            window.clarity('consent', 'update', {
              ad_storage: 'denied',
              analytics_storage: 'denied'
            });
            console.log('✓ Clarity consent set to denied via API');
          } catch (e) {
            console.error('Failed to update Clarity consent:', e);
          }
        }
        
        // Aggressively remove ALL Clarity components
        self.removeClarityCompletely();
        
        // Double-check: Clear any lingering cookies immediately
        self.clearClarityCookies();
        
        console.log('✓ Clarity completely blocked and removed');
        console.log('✓ isInitialized:', self.isInitialized);
        console.log('✓ clarityScriptLoaded:', self.clarityScriptLoaded);
        
        // Mark as interacted and ensure Clarity stays disabled
        userHasInteracted = true;
        self.isInitialized = false;
      } else if (analyticsConsent === 'granted' && self.clarityScriptLoaded && typeof window.clarity === 'function') {
        // Script already loaded, just update consent
        const clarityConsent = {
          ad_storage: marketingConsent,
          analytics_storage: analyticsConsent
        };
        window.clarity('consent', 'update', clarityConsent);
        self.isInitialized = true;
        console.log('Clarity consent updated:', clarityConsent);
      }
      
      console.log('Clarity tracking enabled:', self.isInitialized);
    };

    // Check if Osano is available, if not, retry
    const checkOsano = () => {
      if (typeof window.Osano !== 'undefined' && window.Osano.cm) {
        // Listen for user's explicit consent action (Accept/Deny button click)
        window.Osano.cm.addEventListener('osano-cm-consent-saved', function(consent: any) {
          // CRITICAL: consent-saved fires in two cases:
          // 1. User clicks Accept/Deny button (TRUE user action)
          // 2. Osano loads stored consent from cookies on page load (NOT a user action in this session)
          // If consent-saved fires BEFORE initialized, it's stored/default consent being replayed, not a user action
          const isActualUserAction = osanoHasInitialized;
          
          console.log('======= Osano consent saved event fired =======');
          console.log('Osano has initialized:', osanoHasInitialized);
          console.log('Is actual user action in this session:', isActualUserAction);
          console.log('Current consent state:', consent);
          
          if (isActualUserAction) {
            userHasInteracted = true;
            // Reset consentAlreadyHandled for each new user action to allow multiple changes
            consentAlreadyHandled = true;
            sendClarityConsent(consent, true);
          } else {
            console.warn('Consent saved fired before initialization - this is stored/default consent, not a user action. Ignoring.');
          }
        });

        // Listen for Osano initialization (fires with stored/default consent)
        window.Osano.cm.addEventListener('osano-cm-initialized', function(consent: any) {
          console.log('Osano initialized event fired');
          osanoHasInitialized = true;
          
          // Skip if consent-saved already fired (user clicked Accept/Deny in this session)
          if (consentAlreadyHandled) {
            console.log('Consent already handled by user action - skipping initialized event');
            return;
          }
          // This fires on page load with default/stored consent - NOT a user action
          sendClarityConsent(consent, false);
        });
        
        console.log('Osano consent listeners registered for Clarity.');
      } else {
        console.warn('Osano is not loaded yet. Retrying...');
        // Retry after 500ms if Osano isn't loaded
        setTimeout(checkOsano, 500);
      }
    };
    
    checkOsano();
  }

  public logEvent(name: string, payload: any): void {
    try {
      if (this.isInitialized && typeof window.clarity === 'function') {
        const eventName = `${payload["stage_name"]} - ${payload["action_value"]} - ${name}`;
        window.clarity('event', eventName);
      }
    } catch (e) {
      console.error("Clarity event tracking failed.");
    }
  }

  public bootUser(response: Object, pageID): void {
    // Only proceed if Clarity is initialized (consent granted)
    if (!this.isInitialized || typeof window.clarity !== 'function') {
      return;
    }

    // Add aditional checks for response["profile_id"], response["account"] and response["package"]. If undefined then return
    if (!response || !response["profile_id"] || !response["account"] || !response["package"]) {
      console.error("User info could not be set for Clarity.");
      return;
    }

    try {
      window.clarity('identify', response["key"], response["id"], pageID, response["key"]);
      
      window.clarity('set', "inviteId", response["id"] ?? "");
      window.clarity('set', "profileId", response["profile_id"] ?? "");
      window.clarity('set', "accountId", response["account"]?.["id"] ?? "");
      window.clarity('set', "packageCode", response["package"]?.["code"] ?? "");
      window.clarity('set', "packageName", response["package"]?.["name"] ?? "");
      window.clarity('set', "inviteKey", response["key"] ?? "");
      
      this._localStorage.setItem('isClarityBooted', true);
    } catch (e) {
      console.error("Clarity boot has failed.");
    }
  }

  public shutdown(): void {
    this.logEvent("shutdown", "Shutting down Clarity service.");
  }

  public updateState(newPage: string): void {
    const eventName = `updateState: ${newPage}`;
    this.logEvent(eventName, "Updating Clarity state.");
  }

  public upgrade(upgradeReason: string): void {
    if (this.isInitialized && typeof window.clarity === 'function') {
      window.clarity('upgrade', upgradeReason);
    }
  }

  private isClarityIdValid(clarityId: string): boolean {
    if (clarityId && typeof clarityId === "string" && clarityId.length > 0) {
      return true;
    }
    console.error("Clarity Id is not set properly.");
    return false;
  }
  
  isClarityBooted() {
    return this._localStorage.getItem("isClarityBooted") === true;
  }
}
